import { useMemo, useState } from 'react';
import {
  Cpu,
  ShieldAlert,
  Activity,
  Users,
  Zap,
  PowerOff,
  BatteryWarning,
} from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { trpc } from '../lib/trpc.ts';
import { KPI } from '../components/KPI.tsx';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useLiveStream } from '../components/LiveStream.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { Modal } from '../components/Modal.tsx';
import { Reveal, Stagger, StaggerItem } from '../components/motion.tsx';
import { relativeTime, timeOfDay } from '../lib/format.ts';
import { eventLabels } from '@cdvi-edge/shared';
import './Dashboard.css';

type ActivityEvent = {
  id?: string;
  occurredAt: string;
  kind: string;
  severity: string;
  payload?: unknown;
};

function severityTone(severity: string): 'danger' | 'warn' | 'info' {
  return severity === 'critical' ? 'danger' : severity === 'warn' ? 'warn' : 'info';
}

export function Dashboard() {
  const status = trpc.controller.status.useQuery();
  const rollup = trpc.event.rollup.useQuery({ hours: 24 });
  const hourly = trpc.event.hourly.useQuery();
  const cardholders = trpc.cardholder.list.useQuery({ limit: 1 });
  const recent = trpc.event.list.useQuery({ limit: 10 });
  const live = useLiveStream();
  const reduce = useReducedMotion();

  const [selected, setSelected] = useState<ActivityEvent | null>(null);

  // Merge live events on top of recent.list, dedup by id.
  const recentMerged = useMemo(() => {
    const fromQuery = recent.data ?? [];
    const seen = new Set<string>();
    const out: Array<{
      id?: string;
      occurredAt: string;
      kind: string;
      severity: string;
      payload?: unknown;
    }> = [];
    for (const e of live.events) {
      const key = `${e.occurredAt}-${e.kind}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e as unknown as (typeof out)[number]);
    }
    for (const e of fromQuery) {
      const key = `${e.occurredAt}-${e.kind}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
    return out.slice(0, 10);
  }, [recent.data, live.events]);

  const ctrl = status.data ?? { total: 0, online: 0, acFault: 0, batteryFault: 0, tamperOpen: 0 };
  const events24 = (rollup.data?.info ?? 0) + (rollup.data?.warn ?? 0) + (rollup.data?.critical ?? 0);

  const cardholderCount = cardholders.data?.length ?? 0;

  return (
    <div className="stack dashboard">
      <Reveal className="dash-eyebrow">
        <div className="section-eyebrow">System telemetry</div>
        <h1 className="dash-title">
          Operations <span className="serif-accent">overview</span>
        </h1>
      </Reveal>

      <Stagger className="grid grid-3" gap={0.06}>
        <StaggerItem>
          <KPI
            label="Controllers"
            value={`${ctrl.online} / ${ctrl.total}`}
            sub="online"
            icon={Cpu}
            tone={
              ctrl.online === ctrl.total && ctrl.total > 0
                ? 'ok'
                : ctrl.total > 0
                  ? 'warn'
                  : 'default'
            }
          />
        </StaggerItem>

        <StaggerItem>
          <KPI
            label="Critical (24 h)"
            count={rollup.data?.critical ?? 0}
            sub={`${rollup.data?.warn ?? 0} warnings`}
            icon={ShieldAlert}
            tone={rollup.data?.critical ? 'danger' : 'default'}
          />
        </StaggerItem>

        <StaggerItem>
          <KPI
            label="Events (24 h)"
            count={events24}
            countLocale
            sub="last 24 hours, hourly"
            icon={Activity}
            accessory={
              hourly.data && hourly.data.length > 0 ? (
                <Sparkline values={hourly.data} height={28} width={120} />
              ) : null
            }
          />
        </StaggerItem>

        <StaggerItem>
          <KPI
            label="Cardholders"
            value={cardholderCount === 0 ? 0 : '—'}
            sub="Total in directory"
            icon={Users}
          />
        </StaggerItem>

        <StaggerItem>
          <KPI
            label="AC mains"
            value={ctrl.acFault > 0 ? `${ctrl.acFault} fault` : 'OK'}
            icon={Zap}
            tone={ctrl.acFault ? 'danger' : 'ok'}
          />
        </StaggerItem>

        <StaggerItem>
          <KPI
            label="Battery"
            value={ctrl.batteryFault > 0 ? `${ctrl.batteryFault} fault` : 'OK'}
            icon={BatteryWarning}
            tone={ctrl.batteryFault ? 'warn' : 'ok'}
          />
        </StaggerItem>
      </Stagger>

      <Reveal delay={0.05}>
        <div className="card dash-activity">
          <div className="card-header">
            <Activity size={14} className="muted" />
            <h2>Recent activity</h2>
            <span className="spacer" />
            {live.connected ? (
              <span className="dash-live">
                <span className="live-dot" />
                <Badge tone="ok">live</Badge>
              </span>
            ) : (
              <Badge tone="warn">offline</Badge>
            )}
          </div>
          {recentMerged.length === 0 ? (
            <EmptyState
              icon={PowerOff}
              title="No events yet"
              body="Once a controller comes online, its events will land here in real time."
            />
          ) : (
            <table className="table dash-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Severity</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {recentMerged.map((e, i) => {
                    const tone = severityTone(e.severity);
                    return (
                      <motion.tr
                        key={`${e.occurredAt}-${e.kind}-${i}`}
                        className={`dash-row sev-${tone}`}
                        layout={!reduce}
                        initial={reduce ? { opacity: 0 } : { opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={reduce ? { opacity: 0 } : { opacity: 0, x: 8 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                        onClick={() => setSelected(e)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Inspect event ${eventLabels[e.kind as keyof typeof eventLabels] ?? e.kind}`}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            setSelected(e);
                          }
                        }}
                      >
                        <td className="mono dim" title={e.occurredAt}>
                          {timeOfDay(e.occurredAt)}
                          <div className="dim dash-rel">{relativeTime(e.occurredAt)}</div>
                        </td>
                        <td className="dash-kind">
                          {eventLabels[e.kind as keyof typeof eventLabels] ?? e.kind}
                        </td>
                        <td>
                          <Badge tone={tone}>{e.severity}</Badge>
                        </td>
                        <td className="dim mono dash-detail">
                          {e.payload ? JSON.stringify(e.payload) : ''}
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          )}
        </div>
      </Reveal>

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title="Event detail"
        maxWidth={560}
      >
        {selected && (
          <div className="stack dash-event-detail">
            <div className="grid grid-2 dash-event-meta">
              <div>
                <div className="section-eyebrow">Event</div>
                <div className="dash-event-kind">
                  {eventLabels[selected.kind as keyof typeof eventLabels] ?? selected.kind}
                </div>
                <div className="dim mono dash-event-rawkind">{selected.kind}</div>
              </div>
              <div>
                <div className="section-eyebrow">Severity</div>
                <Badge tone={severityTone(selected.severity)}>{selected.severity}</Badge>
              </div>
              <div>
                <div className="section-eyebrow">Time</div>
                <div className="mono">{timeOfDay(selected.occurredAt)}</div>
                <div className="dim dash-rel">{relativeTime(selected.occurredAt)}</div>
              </div>
              <div>
                <div className="section-eyebrow">Timestamp</div>
                <div className="mono dim dash-event-iso">{selected.occurredAt}</div>
              </div>
            </div>

            <div>
              <div className="section-eyebrow dash-payload-label">Payload</div>
              <pre className="mono dash-payload">
                {selected.payload
                  ? JSON.stringify(selected.payload, null, 2)
                  : 'No payload attached to this event.'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
